import { useMutation } from "@apollo/client";
import { Alert, Box, Button, Divider, Drawer, Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { CommandButton } from "components/CommandButton";
import { ConfirmDialog } from "components/ConfirmDialog";
import {
  FeatureFlagTogglePanel,
  FlagState,
  PendingChangesHint,
  PendingValue,
  collectDependencyKeysToActivate,
  collectDependencyKeysToDeactivate,
  countPendingChanges,
} from "components/FeatureFlags/FeatureFlagTogglePanel";
import {
  modulesEnabledLabels,
  TenantFeatureFlagChips,
} from "components/FeatureFlags/TenantFeatureFlagChips";
import { TENANT_FEATURE_FLAG_FILTER_COLUMNS } from "components/FeatureFlags/tenantFlagFilterColumns";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import {
  FeatureFlagKey,
  getFeatureFlagMeta,
  KNOWN_FEATURE_FLAG_KEYS,
} from "encamp-shared/src/featureFlags";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useCallback, useMemo, useState } from "react";

import { gql } from "../../../generated-graphql";
import {
  TenantsFeatureFlagsQuery,
  TenantsFeatureFlagsQueryVariables,
} from "../../../generated-graphql/graphql";
import { FeatureFlagsTab } from "../Implementation/OrganizationDetails/FeatureFlags";

// Exported because the cross-tenant FlagsTab tests want to share the same
// row → tenantConfigs shape when stubbing the query response.
export const TENANTS_FEATURE_FLAGS_QUERY = gql(`
  query TenantsFeatureFlags($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    tenants(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        name
        isDemo
        isPartner
        isDisabled
        tenantConfigs {
          id
          tenantId
          key
          value
        }
      }
      count
    }
  }
`);

const BULK_SET_FLAGS_MUTATION = gql(`
  mutation BulkSetTenantFeatureFlags($inputs: [SetTenantFeatureFlagInput!]!) {
    setTenantFeatureFlags(inputs: $inputs) {
      id
      tenantConfigs {
        id
        tenantId
        key
        value
      }
    }
  }
`);

type TenantConfigRow = {
  id: string;
  tenantId: string;
  key: string;
  value: boolean;
};

type TenantRow = {
  id: string;
  name: string;
  isDemo: boolean;
  isPartner: boolean;
  isDisabled: boolean;
  tenantConfigs: TenantConfigRow[];
};

/**
 * Tenant-centric (one row per organization) view of feature flags. This is
 * the original Staff Feature Flags page — wrapped here as the
 * `?tab=organizations` panel of the new tabbed layout in `index.tsx`.
 * Behavior is unchanged from before the tab refactor.
 */
export function OrganizationsTab() {
  const alerts = useAlerts();
  const [selectedRows, setSelectedRows] = useState<TenantRow[]>([]);
  const [pendingChanges, setPendingChanges] = useState<
    Partial<Record<FeatureFlagKey, PendingValue>>
  >({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Row-level drill-in: clicking a row (not the checkbox) opens a
  // right-side drawer scoped to that single tenant. `checkboxSelection`
  // in MUI DataGrid already suppresses `onRowClick` for clicks inside
  // the checkbox cell, so single-tenant drill-in and bulk-select don't
  // conflict.
  const [viewingTenant, setViewingTenant] = useState<TenantRow | null>(null);

  const [bulkSet, { loading: applyingBulk }] = useMutation(BULK_SET_FLAGS_MUTATION);

  const openDrawer = () => {
    setPendingChanges({});
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setPendingChanges({});
  };

  const currentStates = useMemo<Partial<Record<FeatureFlagKey, FlagState>>>(
    () => computeCurrentStatesAcrossTenants(selectedRows),
    [selectedRows]
  );

  // Flatten pending changes across selected tenants, skipping no-ops (tenant
  // already at the desired value via TenantConfig or defaultValue).
  const pendingInputs = useMemo(() => {
    const inputs: { tenantId: string; key: FeatureFlagKey; value: boolean }[] = [];
    for (const tenant of selectedRows) {
      for (const entry of Object.entries(pendingChanges) as [FeatureFlagKey, PendingValue][]) {
        const [key, next] = entry;
        if (next === undefined) continue;
        const existing = tenant.tenantConfigs.find((c) => c.key === key);
        const existingValue = existing?.value ?? getFeatureFlagMeta(key).defaultValue;
        if (existingValue === next) continue;
        inputs.push({ tenantId: tenant.id, key, value: next });
      }
    }
    return inputs;
  }, [selectedRows, pendingChanges]);

  const touchesMeasurements = pendingInputs.some((i) => i.key === FeatureFlagKey.Measurements);

  const applyChanges = useCallback(async () => {
    try {
      await bulkSet({ variables: { inputs: pendingInputs } });
      alerts.success(
        `Updated ${pendingInputs.length} flag ${
          pendingInputs.length === 1 ? "setting" : "settings"
        } across ${selectedRows.length} ${selectedRows.length === 1 ? "tenant" : "tenants"}.`
      );
      setConfirmOpen(false);
      closeDrawer();
    } catch (err) {
      alerts.error("Failed to apply feature flag changes", err);
    }
  }, [alerts, bulkSet, pendingInputs, selectedRows.length]);

  const columns: OmnisearchGridColDef<TenantRow>[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "Organization",
        flex: 1,
      },
      {
        field: "modules",
        headerName: "Modules enabled",
        flex: 1.5,
        sortable: false,
        filterable: false,
        valueGetter: (_, row) => modulesEnabledLabels(row.tenantConfigs).join(", "),
        renderCell: ({ row }) => <TenantFeatureFlagChips tenantConfigs={row.tenantConfigs} />,
      },
      {
        field: "isDemo",
        headerName: "Demo",
        flex: 0.4,
        filterKeyType: "boolean",
        renderCell: ({ value }) => (value ? "Yes" : ""),
      },
    ],
    []
  );

  // The Modules / Flags Enabled filter chips are shared with the Staff
  // Organizations page — see `TENANT_FEATURE_FLAG_FILTER_COLUMNS` for
  // the wiring (both alias to the server-side `hasFlag` omnisearch key).
  const additionalFilterColumns = TENANT_FEATURE_FLAG_FILTER_COLUMNS;

  const bulkSetButton = (
    <CommandButton
      key="bulk-set-flags"
      variant="contained"
      disabled={selectedRows.length === 0}
      onClick={openDrawer}
    >
      Bulk Set Flags
      {selectedRows.length > 0 ? ` (${selectedRows.length})` : ""}
    </CommandButton>
  );

  return (
    <>
      <OmnisearchDataGrid<TenantsFeatureFlagsQuery, TenantsFeatureFlagsQueryVariables, TenantRow>
        persistenceKey="staff-feature-flags-by-tenant"
        isURLDriven
        checkboxSelection
        rowHeight="auto"
        dataQuery={TENANTS_FEATURE_FLAGS_QUERY}
        columns={columns}
        getCount={(data) => data.tenants.count}
        getItems={(data) => data.tenants.items}
        onSelectedRowsChanged={setSelectedRows}
        onRowClick={(params) => setViewingTenant(params.row)}
        initialSortModel={[{ field: "name", sort: "asc" }]}
        // Clicking a row body drills into the single-tenant modal.
        // Only the checkbox cell toggles bulk selection — so the two
        // interactions stay independent.
        disableRowSelectionOnClick
        gridSx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
        commandButtons={[bulkSetButton]}
        additionalFilterColumns={additionalFilterColumns}
      />

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        // Always reserve space for the scrollbar on the drawer's
        // scrolling surface. Without this, expanding/collapsing module
        // accordions toggles vertical overflow on/off and the
        // scrollbar appearance reflows the content horizontally —
        // every interaction visibly shifts the toggle column.
        slotProps={{ paper: { sx: { scrollbarGutter: "stable" } } }}
      >
        <Box sx={{ width: { xs: "100vw", md: 560 }, p: 3, bgcolor: "background.paper" }}>
          <Stack spacing={2}>
            <Typography variant="h5">
              Set flags for {selectedRows.length} {selectedRows.length === 1 ? "tenant" : "tenants"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Only flags you change will be updated. Flags you don&apos;t touch keep each selected
              tenant&apos;s existing value.
            </Typography>
            <PendingChangesHint pendingChanges={pendingChanges} />
            <FeatureFlagTogglePanel
              mode="multi"
              currentStates={currentStates}
              pendingChanges={pendingChanges}
              onChange={(key, next) =>
                setPendingChanges((prev) => {
                  const updated: Partial<Record<FeatureFlagKey, PendingValue>> = { ...prev };
                  if (next === undefined) {
                    delete updated[key];
                    return updated;
                  }
                  updated[key] = next;
                  // Cascade across pending + current state. A flag is
                  // treated as "on" for this check if its pending value
                  // is true or (no pending change) every selected tenant
                  // already has it on — matching the hint the user sees
                  // in the panel. For "off", the inverse.
                  const isOn = (k: FeatureFlagKey): boolean => {
                    if (updated[k] !== undefined) return updated[k] === true;
                    return currentStates[k] === true;
                  };
                  if (next === true) {
                    // Turn on parents + `requires` deps so the newly
                    // enabled flag functions.
                    for (const depKey of collectDependencyKeysToActivate(key, isOn)) {
                      updated[depKey] = true;
                    }
                  } else {
                    // Turn off children + anything that requires this
                    // flag, so we never leave dependents inactive.
                    for (const depKey of collectDependencyKeysToDeactivate(key, isOn)) {
                      updated[depKey] = false;
                    }
                  }
                  return updated;
                })
              }
            />
            <Divider />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={closeDrawer}>Cancel</Button>
              <Button
                variant="contained"
                disabled={countPendingChanges(pendingChanges) === 0 || pendingInputs.length === 0}
                onClick={() => setConfirmOpen(true)}
              >
                Review changes
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Drawer>

      <ConfirmDialog
        open={confirmOpen}
        header="Apply feature flag changes?"
        confirmText={`Apply (${pendingInputs.length})`}
        loading={applyingBulk}
        onClose={() => setConfirmOpen(false)}
        onConfirm={applyChanges}
      >
        <Stack spacing={1}>
          <Typography variant="body2">
            This will write <strong>{pendingInputs.length}</strong> flag{" "}
            {pendingInputs.length === 1 ? "change" : "changes"} across{" "}
            <strong>{selectedRows.length}</strong>{" "}
            {selectedRows.length === 1 ? "tenant" : "tenants"}.
          </Typography>
          {touchesMeasurements && (
            <Alert severity="info">
              Toggling the Measurements flag triggers chemical aggregation for each affected tenant
              across all active reporting years.
            </Alert>
          )}
        </Stack>
      </ConfirmDialog>

      <Drawer
        anchor="right"
        open={viewingTenant !== null}
        onClose={() => setViewingTenant(null)}
        slotProps={{ paper: { sx: { scrollbarGutter: "stable" } } }}
      >
        <Box sx={{ width: { xs: "100vw", md: 560 }, p: 3, bgcolor: "background.paper" }}>
          {viewingTenant && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="h5">{viewingTenant.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Feature flags
                </Typography>
              </Box>
              <FeatureFlagsTab tenantId={viewingTenant.id} />
            </Stack>
          )}
        </Box>
      </Drawer>
    </>
  );
}

/**
 * Given N selected tenants, derive the per-flag tri-state:
 *   - `true`    if every tenant currently has the flag enabled
 *   - `false`   if every tenant currently has the flag disabled
 *   - `"mixed"` otherwise
 * Tenants missing a TenantConfig row fall back to the flag's
 * `defaultValue` from `getFeatureFlagMeta`.
 */
export function computeCurrentStatesAcrossTenants(
  tenants: Pick<TenantRow, "tenantConfigs">[]
): Partial<Record<FeatureFlagKey, FlagState>> {
  const states: Partial<Record<FeatureFlagKey, FlagState>> = {};
  if (tenants.length === 0) return states;

  for (const key of KNOWN_FEATURE_FLAG_KEYS) {
    let sawTrue = false;
    let sawFalse = false;
    for (const tenant of tenants) {
      const existing = tenant.tenantConfigs.find((c) => c.key === key);
      const value = existing?.value ?? getFeatureFlagMeta(key).defaultValue;
      if (value) sawTrue = true;
      else sawFalse = true;
      if (sawTrue && sawFalse) break;
    }
    if (sawTrue && sawFalse) states[key] = "mixed";
    else if (sawTrue) states[key] = true;
    else states[key] = false;
  }
  return states;
}
